/**
 * Fluxo de caixa: para onde o dinheiro vai e de onde vem, período a período, com o saldo projetado.
 *
 * Não existe tabela de saldo: o fluxo é CALCULADO a cada abertura a partir das contas a pagar e
 * das contas a receber (`packages/financial/src/cashflow.ts`), então nunca diverge delas.
 *  - parcela/conta já baixada entra na data da baixa; em aberto entra no vencimento (previsto);
 *  - o que está vencido e em aberto é projetado em HOJE e fica destacado como atraso;
 *  - o saldo parte do "saldo inicial" informado aqui (a conta bancária no dia em que você começou
 *    a usar o controle) — sem ele o saldo projetado seria só a soma dos movimentos.
 */
import { createElement as h, useState, type ReactElement } from "react";
import { ArrowDownRight, ArrowUpRight, CircleAlert, Landmark, Scale } from "lucide-react";
import { MiniColumnChart } from "../components/MiniChart.js";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatBrl } from "../format.js";
import { addDays, addMonths, centsToInput, formatDate, signedBrlToCents, todayIso } from "../finance/format.js";
import { buildCashflow, type CashflowGranularity } from "../../../../packages/financial/src/cashflow.js";
import type { FinanceSettings, Payable, Receivable } from "../finance/types.js";
import { useFinanceMutations, useFinanceSettings, usePayables, useReceivables } from "../queries.js";
import { Badge, Button, Card, CardHeader, Drawer, EmptyState, Field, PageHeader, Segmented, StatTile, controlClass } from "../ui/index.js";

export type CashflowRange = "next30" | "next90" | "past30" | "next6m";

const RANGES: Array<{ value: CashflowRange; label: string }> = [
  { value: "next30", label: "Próximos 30 dias" },
  { value: "next90", label: "Próximos 90 dias" },
  { value: "next6m", label: "Próximos 6 meses" },
  { value: "past30", label: "Últimos 30 dias" }
];

const DEFAULT_GRANULARITY: Record<CashflowRange, CashflowGranularity> = { next30: "day", past30: "day", next90: "week", next6m: "month" };

export function rangeBounds(range: CashflowRange, today: string): { from: string; to: string } {
  if (range === "past30") return { from: addDays(today, -29), to: today };
  if (range === "next30") return { from: today, to: addDays(today, 29) };
  if (range === "next90") return { from: today, to: addDays(today, 89) };
  return { from: today, to: addDays(addMonths(today, 6), -1) };
}

function bucketLabel(key: string, granularity: CashflowGranularity, startDate: string): string {
  if (granularity === "month") {
    const [y, m] = key.split("-");
    return `${m}/${(y ?? "").slice(2)}`;
  }
  const [, m, d] = startDate.split("-");
  return `${d}/${m}`;
}

export interface FluxoCaixaViewProps {
  /** `null` = a rota do financeiro não está publicada neste ambiente. */
  payables: Payable[] | null;
  receivables: Receivable[] | null;
  settings: FinanceSettings | null;
  today?: string;
  initialRange?: CashflowRange;
  onSaveSettings?: (settings: FinanceSettings) => void;
  saving?: boolean;
  error?: string | null;
}

function SettingsForm({ settings, today, onSubmit, busy }: { settings: FinanceSettings; today: string; onSubmit: (settings: FinanceSettings) => void; busy: boolean }): ReactElement {
  const [balance, setBalance] = useState(centsToInput(settings.openingBalanceCents));
  const [date, setDate] = useState(settings.openingDate || today);
  const cents = signedBrlToCents(balance);
  return h(
    "form",
    {
      className: "flex flex-col gap-4",
      "data-form": "saldo-inicial",
      onSubmit: (event: { preventDefault: () => void }) => {
        event.preventDefault();
        if (cents !== null && /^\d{4}-\d{2}-\d{2}$/.test(date)) onSubmit({ openingBalanceCents: cents, openingDate: date });
      }
    },
    h("p", { className: "m-0 text-sm text-muted" }, "Informe quanto havia no caixa (somando as contas bancárias) em uma data. A partir dela o sistema soma as entradas e saídas e projeta o saldo. Pode ser negativo."),
    h(Field, { label: "Saldo nessa data (R$)", children: ({ id }) => h("input", { id, className: controlClass, inputMode: "decimal", value: balance, onChange: (e: { target: { value: string } }) => setBalance(e.target.value), required: true }) }),
    h(Field, { label: "Data do saldo", children: ({ id }) => h("input", { id, className: controlClass, type: "date", value: date, onChange: (e: { target: { value: string } }) => setDate(e.target.value), required: true }) }),
    h(Button, { type: "submit", variant: "primary", disabled: cents === null || busy }, busy ? "Salvando…" : "Salvar saldo inicial")
  );
}

export function FluxoCaixaView({ payables, receivables, settings, today = todayIso(), initialRange = "next30", onSaveSettings, saving = false, error = null }: FluxoCaixaViewProps): ReactElement {
  const [range, setRange] = useState<CashflowRange>(initialRange);
  const [granularity, setGranularity] = useState<CashflowGranularity>(DEFAULT_GRANULARITY[initialRange]);
  const [editing, setEditing] = useState(false);
  const header = h(PageHeader, {
    title: "Fluxo de caixa",
    description: "Entradas, saídas e saldo projetado, calculados a partir das contas a pagar e a receber.",
    actions: onSaveSettings && settings ? h(Button, { onClick: () => setEditing(true), "data-action": "saldo-inicial" }, h(Landmark, { className: "h-4 w-4", "aria-hidden": true }), "Saldo inicial") : undefined
  });

  if (payables === null || receivables === null || settings === null) {
    return h("div", { className: "page page--fluxo-caixa flex flex-col gap-6", "data-page": "fluxo-caixa" }, header,
      h(StatusPanel, { kind: "awaiting_source", title: `Fluxo de caixa ${AWAITING_CONTRACT_LABEL}`, detail: "As rotas /api/platform/finance/* não responderam neste ambiente; sem contas a pagar e a receber não há fluxo." }));
  }

  const { from, to } = rangeBounds(range, today);
  const flow = buildCashflow({
    openingBalanceCents: settings.openingBalanceCents,
    openingDate: settings.openingDate,
    today,
    from,
    to,
    granularity,
    payables: payables.map((p) => ({ id: p.id, description: p.description, amountCents: p.amountCents, dueDate: p.dueDate, status: p.status, paidAt: p.paidAt, paidAmountCents: p.paidAmountCents })),
    receivables: receivables.map((r) => ({ id: r.id, description: r.customerName ?? r.description ?? "", amountCents: r.amountCents, dueDate: r.dueDate, status: r.status, receivedAt: r.receivedAt, receivedAmountCents: r.receivedAmountCents }))
  });
  const negative = flow.lowestBalance !== null && flow.lowestBalance.cents < 0;
  const balanceNotSet = settings.openingBalanceCents === 0;

  return h(
    "div",
    { className: "page page--fluxo-caixa flex flex-col gap-6", "data-page": "fluxo-caixa" },
    header,
    balanceNotSet && onSaveSettings
      ? h(StatusPanel, { kind: "empty", title: "Informe o saldo inicial do caixa", detail: "Sem ele, o saldo projetado é só a soma das entradas e saídas do período. Use o botão “Saldo inicial”." })
      : null,
    error ? h(StatusPanel, { kind: "error", title: "Não foi possível salvar", detail: error }) : null,
    h("div", { className: "flex flex-wrap items-center gap-3" },
      h(Segmented<CashflowRange>, { label: "Período", value: range, options: RANGES, onChange: (value) => { setRange(value); setGranularity(DEFAULT_GRANULARITY[value]); } }),
      h(Segmented<CashflowGranularity>, { label: "Agrupar por", value: granularity, options: [{ value: "day", label: "Dia" }, { value: "week", label: "Semana" }, { value: "month", label: "Mês" }], onChange: setGranularity }),
      h("span", { className: "text-sm text-muted" }, `${formatDate(from)} a ${formatDate(to)}`)
    ),
    h("section", { "aria-label": "Resumo do fluxo de caixa", className: "grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" },
      h(StatTile, { label: "Saldo no início", value: formatBrl(flow.startingBalanceCents), hint: `Saldo informado em ${formatDate(settings.openingDate)} mais o que se moveu depois.`, icon: Landmark, "data-kpi": "fluxo-inicial" }),
      h(StatTile, { label: "Entradas", value: formatBrl(flow.totals.inCents), hint: "Recebido e previsto no período.", icon: ArrowUpRight, "data-kpi": "fluxo-entradas" }),
      h(StatTile, { label: "Saídas", value: formatBrl(flow.totals.outCents), hint: "Pago e previsto no período.", icon: ArrowDownRight, "data-kpi": "fluxo-saidas" }),
      h(StatTile, { label: "Saldo no fim", value: formatBrl(flow.endingBalanceCents), hint: negative ? "Atenção: o saldo fica negativo no período." : "Saldo projetado no último dia.", icon: Scale, "data-kpi": "fluxo-final" })
    ),
    negative && flow.lowestBalance
      ? h(StatusPanel, { kind: "error", title: "Caixa negativo previsto", detail: `O menor saldo previsto é ${formatBrl(flow.lowestBalance.cents)} (período ${flow.lowestBalance.bucketKey}). Antecipe recebimentos ou renegocie vencimentos.` })
      : null,
    flow.overdue.receivableCount + flow.overdue.payableCount > 0
      ? h(Card, { "data-panel": "atrasados" },
          h(CardHeader, { title: "Atrasados", description: "Em aberto e já vencidos. Entram no fluxo de hoje até serem baixados." }),
          h("div", { className: "flex flex-wrap gap-6 text-sm" },
            h("span", { className: "flex items-center gap-2" }, h(CircleAlert, { className: "h-4 w-4 text-warning", "aria-hidden": true }), `A receber atrasado: ${formatBrl(flow.overdue.receivableCents)} (${flow.overdue.receivableCount} parcela(s))`),
            h("span", { className: "flex items-center gap-2" }, h(CircleAlert, { className: "h-4 w-4 text-danger", "aria-hidden": true }), `A pagar atrasado: ${formatBrl(flow.overdue.payableCents)} (${flow.overdue.payableCount} conta(s))`)
          )
        )
      : null,
    h(Card, null,
      h(CardHeader, { title: "Saldo ao fim de cada período", description: "Saldo inicial + tudo que entrou e saiu até o fim do período." }),
      flow.buckets.length === 0
        ? h(EmptyState, { title: "Sem períodos" })
        : h(MiniColumnChart, {
            title: "Saldo projetado ao fim de cada período",
            "data-chart": "fluxo-saldo",
            columns: flow.buckets.map((b) => ({ label: bucketLabel(b.key, granularity, b.startDate), value: b.balanceCents, display: formatBrl(b.balanceCents) }))
          })
    ),
    h(Card, null,
      h(CardHeader, { title: "Período a período" }),
      h("div", { className: "overflow-x-auto" },
        h("table", { className: "w-full text-left text-sm", "data-table": "fluxo-caixa" },
          h("thead", null, h("tr", { className: "text-xs uppercase text-muted" },
            ["Período", "Entradas", "Saídas", "Resultado", "Saldo ao fim"].map((title, index) => h("th", { key: title, scope: "col", className: "px-2 py-2" + (index > 0 ? " text-right" : "") }, title)))),
          h("tbody", null, flow.buckets.map((b) =>
            h("tr", { key: b.key, className: "border-t border-border", "data-bucket": b.key },
              h("td", { className: "px-2 py-2 tabular-nums" }, b.startDate === b.endDate ? formatDate(b.startDate) : `${formatDate(b.startDate)} a ${formatDate(b.endDate)}`,
                b.startDate <= today && today <= b.endDate ? h(Badge, { tone: "info", className: "ml-2" }, "hoje") : null),
              h("td", { className: "px-2 py-2 text-right tabular-nums" }, formatBrl(b.inCents), b.inPendingCents > 0 ? h("span", { className: "block text-xs text-muted" }, `${formatBrl(b.inPendingCents)} previsto`) : null),
              h("td", { className: "px-2 py-2 text-right tabular-nums" }, formatBrl(b.outCents), b.outPendingCents > 0 ? h("span", { className: "block text-xs text-muted" }, `${formatBrl(b.outPendingCents)} previsto`) : null),
              h("td", { className: "px-2 py-2 text-right font-semibold tabular-nums " + (b.netCents < 0 ? "text-danger" : "text-ink") }, formatBrl(b.netCents)),
              h("td", { className: "px-2 py-2 text-right font-extrabold tabular-nums " + (b.balanceCents < 0 ? "text-danger" : "text-ink") }, formatBrl(b.balanceCents))
            )
          ))
        )
      )
    ),
    onSaveSettings
      ? h(Drawer, { open: editing, title: "Saldo inicial do caixa", onClose: () => setEditing(false), children: h(SettingsForm, { settings, today, busy: saving, onSubmit: (value) => { onSaveSettings(value); setEditing(false); } }) })
      : null
  );
}

export function FluxoCaixa(): ReactElement {
  const payables = usePayables();
  const receivables = useReceivables();
  const settings = useFinanceSettings();
  const { saveSettings } = useFinanceMutations();
  const pending = payables.isPending || receivables.isPending || settings.isPending;
  if (pending) return h(StatusPanel, { kind: "loading", title: "Calculando o fluxo de caixa…" });
  const failed = payables.error ?? receivables.error ?? settings.error;
  if (failed) return h(StatusPanel, { kind: "error", title: "Não foi possível carregar o financeiro", detail: errorDetail(failed) });
  return h(FluxoCaixaView, {
    payables: payables.data ?? null,
    receivables: receivables.data ?? null,
    settings: settings.data ?? null,
    onSaveSettings: (value) => saveSettings.mutate(value),
    saving: saveSettings.isPending,
    error: saveSettings.error ? saveSettings.error.message : null
  });
}
